import { Button, TextField } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { currentUserStore } from '../../../CurrentUserStore';
import { NotTranslated, T } from '../../../Translations';
import { useQueryWithDecoder } from '../../../UseQueryWithDecoder';
import { createConnection, serverErrorMessage } from '../Api';
import { ssoConfigurationsResourceDecoder } from '../Decoders';
import Breadcrumb from '../Breadcrumb';
import Loading from '../Loading';
import PageLayout from '../PageLayout';
import Stepper from '../Stepper';
import { SsoEnvironment, SsoVendor, SubmitFailure } from '../Types';
import EnvironmentPicker from './EnvironmentPicker';
import VendorPicker from './VendorPicker';

type State =
  | {
      kind: 'editing';
      displayName: string;
      vendor: SsoVendor;
      environment: SsoEnvironment;
      error: SubmitFailure;
    }
  | { kind: 'submitting'; displayName: string; vendor: SsoVendor; environment: SsoEnvironment };

const initialState: State = {
  kind: 'editing',
  displayName: '',
  vendor: 'entra',
  environment: 'production',
  error: nothing(),
};

function AddConnection() {
  const [state, setState] = React.useState<State>(initialState);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQueryWithDecoder(
    'sso-configurations',
    currentUserStore.links,
    ssoConfigurationsResourceDecoder,
  );

  const submit = () => {
    if (state.kind !== 'editing' || !data) {
      return;
    }
    const { displayName, vendor, environment } = state;
    setState({ kind: 'submitting', displayName, vendor, environment });
    createConnection(data.links, { displayName, vendor, environment }).fork(
      (err) =>
        setState({
          kind: 'editing',
          displayName,
          vendor,
          environment,
          error: just(serverErrorMessage(err)),
        }),
      (connection) => {
        queryClient.invalidateQueries({ queryKey: ['sso-configurations'] });
        navigate(`/single_sign_on/connections/${connection.payload.id}`);
      },
    );
  };

  return (
    <PageLayout>
      <Breadcrumb>
        <T kind="Add identity provider" />
      </Breadcrumb>
      <h2 className="text-2xl font-semibold text-foreground">
        <T kind="Add identity provider" />
      </h2>
      <p className="mt-1 max-w-3xl text-muted-foreground">
        <T kind="This creates your connection and generates the details your IdP administrator needs in the next step." />
      </p>
      <div className="mt-6">
        <Stepper current={1} />
      </div>
      {isLoading || !data ? (
        <div className="mt-6">
          <Loading />
        </div>
      ) : (
        <div className="mt-6 max-w-4xl rounded-lg border border-border bg-card p-6">
          <div className="max-w-md">
            <TextField
              label={<T kind="Connection name" />}
              value={state.displayName}
              onChange={(event) =>
                state.kind === 'editing' && setState({ ...state, displayName: event.target.value })
              }
              helperText={
                <T kind='A name your team will recognize - it appears in the domain assignment list. For example "Corporate Entra ID" or "Contractor Okta".' />
              }
              inputClassName="border-none"
              fullWidth
              data-intercom-target="sso-connection-name-input-field"
            />
          </div>
          <div className="mt-6">
            <span className="mb-2 block text-sm font-semibold text-foreground">
              <T kind="Which identity provider is this?" />
            </span>
            <VendorPicker
              selected={state.vendor}
              onSelect={(vendor) => state.kind === 'editing' && setState({ ...state, vendor })}
            />
          </div>
          <div className="mt-6 max-w-sm">
            <EnvironmentPicker
              selected={state.environment}
              onSelect={(environment) =>
                state.kind === 'editing' && setState({ ...state, environment })
              }
            />
          </div>
          {state.kind === 'editing' &&
            state.error
              .map((message) => (
                <p key="error" className="mt-4 text-sm text-destructive">
                  {message
                    .map((text) => <NotTranslated key="server" text={text} />)
                    .getOrElse(() => (
                      <T kind="We are unable to process your request at this time" />
                    ))}
                </p>
              ))
              .getOrElse(() => <></>)}
          <div className="mt-6 flex justify-end gap-3 border-t border-border pt-5">
            <Button color="primary" variant="outlined" onClick={() => navigate('/single_sign_on')}>
              <T kind="Cancel" />
            </Button>
            <Button
              color="success"
              isLoading={state.kind === 'submitting'}
              disabled={state.displayName.trim().length === 0 || state.kind === 'submitting'}
              onClick={submit}
              data-intercom-target="sso-create-connection-button"
            >
              <T kind="Create connection" />
            </Button>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default observer(AddConnection);
